/**
 * Demodata: een compleet nepevenement waarmee je in twee minuten het hele
 * verhaal laat zien.
 *
 * De omzet verloopt zoals op een echte festivaldag: rustig aan het begin van de
 * middag, druk tussen zes en negen, daarna uitlopend. Een dashboard met een
 * vlakke lijn overtuigt niemand.
 *
 * Alles is deterministisch als je een eigen toevalsfunctie meegeeft, zodat een
 * test er iets over kan zeggen.
 */

import { BTW_HOOG, BTW_LAAG, type BtwTarief } from './btw.js';
import { BONNUMMER_MAX } from './bonnummer.js';
import { maakInlogcode } from './inlogcode.js';
import type { Basispunten, Cent } from './geld.js';

export interface DemoProduct {
  naam: string;
  prijsCent: Cent;
  btwTarief: BtwTarief;
  categorie: string;
  bekers: number;
}

export interface DemoKraam {
  naam: string;
  commissieBasispunten: Basispunten;
  inlogcode: string;
  producten: DemoProduct[];
}

export interface DemoBonregel {
  productIndex: number;
  aantal: number;
}

export interface DemoBon {
  kraamIndex: number;
  nummer: number;
  /** Tijdstip in UTC. */
  aangeslagenOp: string;
  regels: DemoBonregel[];
  ingeleverdeBekers: number;
}

export interface Demodataset {
  evenement: {
    naam: string;
    locatie: string;
    startOp: string;
    eindOp: string;
    bekerborgCent: Cent;
    dagtariefKraamsetCent: Cent;
    doorbelastPerBonTiendeCent: number;
    inkoopOverOmzetBasispunten: Basispunten;
    doorbelastOverOmzetBasispunten: Basispunten;
  };
  kramen: DemoKraam[];
  bonnen: DemoBon[];
}

/** Kleine, voorspelbare toevalsgenerator, zodat een demo twee keer hetzelfde doet. */
export function maakToeval(zaad: number): () => number {
  let stand = zaad >>> 0;
  return () => {
    stand = (stand + 0x6d2b79f5) >>> 0;
    let waarde = Math.imul(stand ^ (stand >>> 15), 1 | stand);
    waarde = (waarde + Math.imul(waarde ^ (waarde >>> 7), 61 | waarde)) ^ waarde;
    return ((waarde ^ (waarde >>> 14)) >>> 0) / 4_294_967_296;
  };
}

const KRAMEN: Omit<DemoKraam, 'inlogcode'>[] = [
  {
    naam: 'Broodje Bakkie',
    commissieBasispunten: 1_000,
    producten: [
      {
        naam: 'Broodje bal',
        prijsCent: 550,
        btwTarief: BTW_LAAG,
        categorie: 'Broodjes',
        bekers: 0,
      },
      {
        naam: 'Broodje kroket',
        prijsCent: 450,
        btwTarief: BTW_LAAG,
        categorie: 'Broodjes',
        bekers: 0,
      },
      { naam: 'Friet groot', prijsCent: 450, btwTarief: BTW_LAAG, categorie: 'Friet', bekers: 0 },
      { naam: 'Mayonaise', prijsCent: 75, btwTarief: BTW_LAAG, categorie: 'Sauzen', bekers: 0 },
    ],
  },
  {
    naam: 'Bar de Dorst',
    commissieBasispunten: 1_200,
    producten: [
      { naam: 'Pils', prijsCent: 350, btwTarief: BTW_HOOG, categorie: 'Bier', bekers: 1 },
      { naam: 'Speciaalbier', prijsCent: 500, btwTarief: BTW_HOOG, categorie: 'Bier', bekers: 1 },
      { naam: 'Wijn wit', prijsCent: 450, btwTarief: BTW_HOOG, categorie: 'Wijn', bekers: 1 },
      { naam: 'Cola', prijsCent: 300, btwTarief: BTW_LAAG, categorie: 'Fris', bekers: 1 },
    ],
  },
  {
    naam: 'Pizza Vulcano',
    commissieBasispunten: 1_000,
    producten: [
      {
        naam: 'Pizza margherita',
        prijsCent: 850,
        btwTarief: BTW_LAAG,
        categorie: 'Pizza',
        bekers: 0,
      },
      { naam: 'Pizza salami', prijsCent: 950, btwTarief: BTW_LAAG, categorie: 'Pizza', bekers: 0 },
      { naam: 'Water', prijsCent: 250, btwTarief: BTW_LAAG, categorie: 'Fris', bekers: 1 },
    ],
  },
  {
    naam: 'IJskraam Zon',
    commissieBasispunten: 900,
    producten: [
      { naam: 'Softijs', prijsCent: 300, btwTarief: BTW_LAAG, categorie: 'IJs', bekers: 0 },
      { naam: 'Milkshake', prijsCent: 450, btwTarief: BTW_LAAG, categorie: 'IJs', bekers: 1 },
    ],
  },
];

/**
 * Hoe druk het per uur is, van 14:00 tot en met 23:00 Amsterdamse tijd.
 * De piek ligt tussen zes en negen, zoals op elk festival.
 */
const DRUKTE_PER_UUR: readonly { uur: number; gewicht: number }[] = [
  { uur: 14, gewicht: 3 },
  { uur: 15, gewicht: 5 },
  { uur: 16, gewicht: 7 },
  { uur: 17, gewicht: 10 },
  { uur: 18, gewicht: 14 },
  { uur: 19, gewicht: 16 },
  { uur: 20, gewicht: 15 },
  { uur: 21, gewicht: 11 },
  { uur: 22, gewicht: 7 },
  { uur: 23, gewicht: 4 },
];

export interface DemoInstellingen {
  /** De dag van het evenement, als 2026-07-04. */
  dag: string;
  evenementnaam?: string;
  aantalKramen?: number;
  bonnenPerKraam?: number;
  toeval?: () => number;
}

/**
 * Zet een uur in Amsterdamse tijd om naar UTC voor die dag. In de zomer is dat
 * twee uur eraf, in de winter één. We rekenen het uit in plaats van het te
 * gokken, want een demo in oktober moet ook kloppen.
 */
function naarUtc(dag: string, uur: number, minuut: number): string {
  // Wat er op de wandklok staat, alsof het UTC was.
  const klok = Date.parse(
    `${dag}T${String(uur).padStart(2, '0')}:${String(minuut).padStart(2, '0')}:00Z`,
  );

  // Hoe laat het in Amsterdam is op een gegeven moment, ook weer als "klok-als-UTC".
  // Met de hele datum en tijd, niet alleen het uur: na middernacht Amsterdamse tijd
  // (uur 00 of 01) gaf het verschil van alleen de uren een dag te laat op bonnen
  // tussen 22:00 en 23:59 (gevonden op 21 september 2026).
  const amsterdamAlsUtc = (moment: number): number => {
    const delen = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(moment));
    const waarde = (soort: string): number =>
      Number(delen.find((deel) => deel.type === soort)?.value);
    return Date.UTC(
      waarde('year'),
      waarde('month') - 1,
      waarde('day'),
      waarde('hour'),
      waarde('minute'),
    );
  };

  // Het verschil met UTC twee keer bepaald: eerst op de gok, dan op het uitgerekende
  // moment, zodat ook een dag met een zomertijdwissel klopt.
  let moment = klok - (amsterdamAlsUtc(klok) - klok);
  moment = klok - (amsterdamAlsUtc(moment) - moment);
  return new Date(moment).toISOString();
}

export function maakDemodataset(instellingen: DemoInstellingen): Demodataset {
  const toeval = instellingen.toeval ?? Math.random;
  const aantalKramen = Math.min(instellingen.aantalKramen ?? 4, KRAMEN.length);
  const bonnenPerKraam = Math.min(instellingen.bonnenPerKraam ?? 60, BONNUMMER_MAX);

  const kramen: DemoKraam[] = KRAMEN.slice(0, aantalKramen).map((kraam) => ({
    ...kraam,
    inlogcode: maakInlogcode({ kraamnaam: kraam.naam, toeval }),
  }));

  const totaalGewicht = DRUKTE_PER_UUR.reduce((som, uur) => som + uur.gewicht, 0);

  const bonnen: DemoBon[] = [];
  kramen.forEach((kraam, kraamIndex) => {
    for (let nummer = 1; nummer <= bonnenPerKraam; nummer += 1) {
      // Het uur volgt de druktecurve, niet een gelijke verdeling.
      let trekking = toeval() * totaalGewicht;
      let uur = DRUKTE_PER_UUR[0]?.uur ?? 14;
      for (const stand of DRUKTE_PER_UUR) {
        trekking -= stand.gewicht;
        if (trekking <= 0) {
          uur = stand.uur;
          break;
        }
      }
      const minuut = Math.trunc(toeval() * 60);

      const aantalRegels = 1 + Math.trunc(toeval() * Math.min(3, kraam.producten.length));
      const gekozen = new Set<number>();
      const regels: DemoBonregel[] = [];
      for (let i = 0; i < aantalRegels; i += 1) {
        const productIndex = Math.trunc(toeval() * kraam.producten.length);
        if (gekozen.has(productIndex)) continue;
        gekozen.add(productIndex);
        regels.push({ productIndex, aantal: 1 + Math.trunc(toeval() * 3) });
      }
      if (regels.length === 0) regels.push({ productIndex: 0, aantal: 1 });

      // Later op de avond komen er bekers terug.
      const bekersTerug = uur >= 20 && toeval() < 0.25 ? 1 + Math.trunc(toeval() * 2) : 0;

      bonnen.push({
        kraamIndex,
        nummer,
        aangeslagenOp: naarUtc(instellingen.dag, uur, minuut),
        regels,
        ingeleverdeBekers: bekersTerug,
      });
    }
  });

  bonnen.sort((a, b) => a.aangeslagenOp.localeCompare(b.aangeslagenOp));

  return {
    evenement: {
      naam: instellingen.evenementnaam ?? 'Demofestival Zomerpark',
      locatie: 'Zomerpark, Amsterdam',
      startOp: instellingen.dag,
      eindOp: instellingen.dag,
      bekerborgCent: 100,
      dagtariefKraamsetCent: 3_500,
      doorbelastPerBonTiendeCent: 66, // € 0,066, het werkelijke Rabo-tarief
      doorbelastOverOmzetBasispunten: 170,
      inkoopOverOmzetBasispunten: 150,
    },
    kramen,
    bonnen,
  };
}
